'use client'

import Link from 'next/link'
import React, { useEffect, useRef, useState } from 'react'

import { ProductArt } from '@/cercatinte/components/ProductArt'
import { formatEuro } from '@/negozio/prezzi'
import { province } from '@/negozio/province'
import type { Destinazione, StimaSpedizione } from '@/negozio/spedizione'
import { mailto } from '@/utilities/siteInfo'

import {
  validaCarrello,
  type CarrelloValidato,
  type OpzioneSpedizione,
  type RigaRimossa,
} from './actions'
import { useCarrello } from './CarrelloProvider.client'
import { chiaveRiga, QUANTITA_MAX, type RigaCarrello } from './righe'

const firma = (righe: RigaCarrello[]) =>
  righe.map((r) => `${chiaveRiga(r)}×${r.quantita}`).join('|')

const opzioniQuantita = (attuale: number) =>
  [...new Set([...Array.from({ length: 20 }, (_, i) => i + 1), attuale])]
    .filter((n) => n <= QUANTITA_MAX)
    .sort((a, b) => a - b)

const descriviTinta = (riga: CarrelloValidato['righe'][number]) =>
  riga.tintaInfo
    ? [riga.tintaInfo.marca, `codice ${riga.tintaInfo.codice}`, riga.tintaInfo.nome, riga.veicolo]
        .filter(Boolean)
        .join(' · ')
    : riga.tintaLibera
      ? [riga.tintaLibera.marca, `codice ${riga.tintaLibera.codice}`, riga.tintaLibera.veicolo]
          .filter(Boolean)
          .join(' · ')
      : null

const nonDisponibile: Record<Extract<StimaSpedizione, { disponibile: false }>['motivo'], string> = {
  'nazione-non-servita': 'Non spedisce in questa nazione.',
  'peso-eccessivo': 'Pacco troppo pesante per questo metodo: ti contattiamo noi.',
  'senza-tariffe': 'Tariffe non ancora disponibili.',
}

const costoSpedizione = (stima: StimaSpedizione) =>
  stima.disponibile
    ? stima.costoCentesimi === 0
      ? 'Gratis'
      : formatEuro(stima.costoCentesimi)
    : '—'

const corpoRichiesta = (
  { righe, totale }: CarrelloValidato,
  spedizione?: OpzioneSpedizione,
  destinazione?: Destinazione,
) =>
  [
    'Buongiorno, vorrei ordinare:',
    '',
    ...righe.map((r) =>
      [
        `${r.quantita} × ${r.prodotto.nome} — ${r.formato.etichetta} (${r.sku})`,
        descriviTinta(r) && `   Tinta: ${descriviTinta(r)}`,
      ]
        .filter(Boolean)
        .join('\n'),
    ),
    '',
    `Totale prodotti: ${formatEuro(totale.lordo)} IVA inclusa`,
    spedizione?.stima.disponibile &&
      `Spedizione: ${spedizione.nome}, stima ${costoSpedizione(spedizione.stima)}${
        destinazione?.provincia || destinazione?.cap
          ? ` (${[destinazione.cap, destinazione.provincia].filter(Boolean).join(' ')})`
          : ''
      }`,
    '',
  ]
    .filter((r) => r !== false && r !== undefined)
    .join('\n')

/**
 * The cart page. Whatever is in localStorage is only a request: every change is sent to
 * `validaCarrello` and the page renders the server's answer (prices, availability, removed
 * lines). The stored cart is then rewritten with the lines the server accepted.
 */
export const Carrello: React.FC = () => {
  const { righe, pronto, impostaQuantita, rimuovi, sostituisci } = useCarrello()
  const [risultato, setRisultato] = useState<CarrelloValidato | null>(null)
  const [rimosse, setRimosse] = useState<RigaRimossa[]>([])
  const [errore, setErrore] = useState(false)
  const [inCorso, setInCorso] = useState(false)
  const richiesta = useRef(0)
  // `undefined` until the customer touches the fields: the server then falls back to the
  // logged-in cliente's default shipping address, and the fields are filled from its answer.
  const [destinazione, setDestinazione] = useState<{ provincia: string; cap: string }>()
  const [capDigitato, setCapDigitato] = useState('')
  const [metodoScelto, setMetodoScelto] = useState<number>()

  useEffect(() => {
    if (!pronto) return
    const id = ++richiesta.current
    setInCorso(true)

    validaCarrello(righe, destinazione)
      .then((r) => {
        if (id !== richiesta.current) return
        setRisultato(r)
        if (!destinazione && r.destinazione) setCapDigitato(r.destinazione.cap ?? '')
        setErrore(false)
        setInCorso(false)
        if (r.rimosse.length) setRimosse((prev) => [...prev, ...r.rimosse])

        const accettate = r.righe.map(({ sku, quantita, tinta, veicolo, tintaLibera }) => ({
          sku,
          quantita,
          ...(tinta && { tinta }),
          ...(veicolo && { veicolo }),
          ...(tintaLibera && { tintaLibera }),
        }))
        if (firma(accettate) !== firma(righe)) sostituisci(accettate)
      })
      .catch(() => {
        if (id !== richiesta.current) return
        setErrore(true)
        setInCorso(false)
      })
    // `sostituisci` is stable enough: it only changes with the provider's state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [righe, pronto, destinazione])

  if (errore && !risultato) {
    return (
      <p className="modalnote" role="alert">
        Non riusciamo a verificare il carrello in questo momento. Riprova tra poco.
      </p>
    )
  }

  if (!risultato) {
    return <p className="modalnote">Verifica del carrello in corso…</p>
  }

  const provinciaAttuale = destinazione?.provincia ?? risultato.destinazione?.provincia ?? ''
  const capAttuale = destinazione?.cap ?? risultato.destinazione?.cap ?? ''
  const aggiornaDestinazione = (modifica: Partial<{ provincia: string; cap: string }>) =>
    setDestinazione({ provincia: provinciaAttuale, cap: capAttuale, ...modifica })

  const disponibili = risultato.spedizione.filter((o) => o.stima.disponibile)
  const spedizione =
    disponibili.find((o) => o.id === metodoScelto) ??
    // Default: the cheapest courier, else whatever is available (pickup).
    [...disponibili]
      .filter((o) => o.tipo === 'corriere')
      .sort(
        (a, b) =>
          (a.stima.disponibile ? a.stima.costoCentesimi : 0) -
          (b.stima.disponibile ? b.stima.costoCentesimi : 0),
      )[0] ??
    disponibili[0]
  const costoScelto =
    spedizione?.stima.disponibile === true ? spedizione.stima.costoCentesimi : null

  return (
    <div aria-busy={inCorso}>
      {rimosse.length > 0 && (
        <div className="modalnote" role="status">
          <b>Alcuni articoli sono stati tolti dal carrello:</b>
          <ul>
            {rimosse.map((r, i) => (
              <li key={i}>
                {r.nome ?? r.riga?.sku ?? 'Articolo'}
                {r.riga?.sku && r.nome ? ` (${r.riga.sku})` : ''} — {r.messaggio}
              </li>
            ))}
          </ul>
        </div>
      )}

      {risultato.righe.length === 0 ? (
        <>
          <p className="modalnote">Il carrello è vuoto.</p>
          <Link className="textlink" href="/negozio">
            Scopri i prodotti →
          </Link>
        </>
      ) : (
        <>
          {risultato.righe.map((riga) => {
            const tinta = descriviTinta(riga)
            return (
              <div className="cartline" data-sku={riga.sku} key={riga.chiave}>
                <ProductArt color={riga.tintaInfo?.hex ?? undefined} type={riga.prodotto.tipoArt} />
                <div className="cartname">
                  <b>
                    <Link href={`/negozio/${riga.prodotto.slug}`}>{riga.prodotto.nome}</Link>
                  </b>
                  <small>
                    {riga.formato.etichetta} · Codice {riga.sku}
                  </small>
                  {tinta && <small>Tinta: {tinta}</small>}
                  <small>
                    {formatEuro(riga.prezzoCentesimi)} cad.
                    {riga.formato.disponibilita === 'su-ordinazione' && ' · Su ordinazione'}
                  </small>
                  {riga.quantitaRidotta && (
                    <small>Quantità ridotta alla disponibilità in magazzino.</small>
                  )}
                  <label>
                    Quantità{' '}
                    <select
                      disabled={inCorso}
                      onChange={(e) => impostaQuantita(riga.chiave, Number(e.target.value))}
                      value={riga.quantita}
                    >
                      {opzioniQuantita(riga.quantita).map((n) => (
                        <option key={n}>{n}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <strong>{formatEuro(riga.totaleCentesimi)}</strong>
                <button
                  aria-label={`Rimuovi ${riga.prodotto.nome}`}
                  className="remove"
                  disabled={inCorso}
                  onClick={() => rimuovi(riga.chiave)}
                  type="button"
                >
                  Rimuovi
                </button>
              </div>
            )
          })}

          <div className="carttotal">
            <span>Totale prodotti</span>
            <strong>{formatEuro(risultato.totale.lordo)}</strong>
          </div>
          <p className="modalnote">
            IVA inclusa ({formatEuro(risultato.totale.iva)}).
            {risultato.prezziIndicativi && ' Alcuni prezzi sono indicativi.'}
          </p>

          {risultato.spedizione.length > 0 ? (
            <fieldset className="shippingestimate">
              <legend>Stima della spedizione</legend>
              <div className="shippingplace">
                <label>
                  Provincia
                  <select
                    onChange={(e) => aggiornaDestinazione({ provincia: e.target.value })}
                    value={provinciaAttuale}
                  >
                    <option value="">—</option>
                    {province.map((p) => (
                      <option key={p.sigla} value={p.sigla}>
                        {p.nome} ({p.sigla})
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  CAP
                  <input
                    autoComplete="postal-code"
                    inputMode="numeric"
                    maxLength={5}
                    onBlur={() => aggiornaDestinazione({ cap: capDigitato })}
                    onChange={(e) => {
                      const cap = e.target.value.replace(/\D/g, '')
                      setCapDigitato(cap)
                      if (cap.length === 5) aggiornaDestinazione({ cap })
                    }}
                    value={capDigitato}
                  />
                </label>
              </div>
              <ul className="shippingoptions">
                {risultato.spedizione.map((opzione) => {
                  const { stima } = opzione
                  return (
                    <li key={opzione.id}>
                      <label className={stima.disponibile ? undefined : 'isdisabled'}>
                        <input
                          checked={spedizione?.id === opzione.id}
                          disabled={!stima.disponibile}
                          name="spedizione"
                          onChange={() => setMetodoScelto(opzione.id)}
                          type="radio"
                        />
                        <span>
                          <b>{opzione.nome}</b>
                          {opzione.tempiConsegna && <small>{opzione.tempiConsegna}</small>}
                          {stima.disponibile ? (
                            <>
                              {stima.gratuita && <small>Spedizione gratuita raggiunta.</small>}
                              {stima.supplementoCentesimi > 0 && (
                                <small>
                                  Incluso supplemento {stima.zona ?? 'zona disagiata'}{' '}
                                  {formatEuro(stima.supplementoCentesimi)}.
                                </small>
                              )}
                              {stima.zonaDaVerificare && (
                                <small>
                                  Indica provincia o CAP: isole e località disagiate hanno un
                                  supplemento.
                                </small>
                              )}
                            </>
                          ) : (
                            <small>{nonDisponibile[stima.motivo]}</small>
                          )}
                        </span>
                        <strong>{costoSpedizione(stima)}</strong>
                      </label>
                    </li>
                  )
                })}
              </ul>
              {costoScelto !== null && (
                <div className="carttotal">
                  <span>Totale stimato con spedizione</span>
                  <strong>{formatEuro(risultato.totale.lordo + costoScelto)}</strong>
                </div>
              )}
              <p className="modalnote">
                Stima calcolata sul peso dei prodotti
                {spedizione?.stima.disponibile && spedizione.stima.pesoIncompleto
                  ? ' (per alcuni manca il peso: la confermiamo noi)'
                  : ''}
                .{' '}
                <Link className="textlink" href="/spedizioni">
                  Tariffe e tempi di consegna
                </Link>
              </p>
            </fieldset>
          ) : (
            <p className="modalnote">
              Spese di spedizione calcolate al passaggio successivo.{' '}
              <Link className="textlink" href="/spedizioni">
                Informazioni sulla spedizione
              </Link>
            </p>
          )}
          {errore && (
            <p className="modalnote" role="alert">
              Ultima verifica non riuscita: i prezzi mostrati potrebbero non essere aggiornati.
            </p>
          )}
          <Link className="primary" href="/cassa">
            Procedi all’ordine <span>→</span>
          </Link>
          <p className="modalnote">
            Nessun pagamento online: dopo l’ordine ti mostriamo come pagare. Preferisci scriverci?{' '}
            <a
              className="textlink"
              href={mailto(
                'Richiesta ordine',
                corpoRichiesta(risultato, spedizione, risultato.destinazione ?? undefined),
              )}
            >
              Invia una richiesta via email
            </a>
            .
          </p>
        </>
      )}
    </div>
  )
}
